import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Tabs, Tab, Typography, Avatar, Pagination, Grid, Skeleton } from '@mui/material';
import { getUserFollowers, getUserFollowing } from '../services/api';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import UserPlanBadge from './common/UserPlanBadge';

interface FollowsTabProps {
  username?: string;
  isPublic: boolean;
}

interface FollowUser {
  username: string;
  display_name: string;
  avatar: string | null;
  bio: string;
  subscription_plan?: number;
}

// タブの種類を定数で定義
const FOLLOWS_TABS = {
  FOLLOWING: 'following',
  FOLLOWERS: 'followers'
} as const;

type FollowsTabType = typeof FOLLOWS_TABS[keyof typeof FOLLOWS_TABS];

const FollowsLoadingPlaceholder = () => (
  <Grid container spacing={1}>
    {[...Array(6)].map((_, index) => (
      <Grid item xs={12} key={index}>
        <Box
          sx={{
            p: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Skeleton variant="circular" width={48} height={48} sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="30%" height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', mb: 0.5 }} />
              <Skeleton variant="text" width="20%" height={16} sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', mb: 1 }} />
              <Skeleton variant="text" width="60%" height={16} sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }} />
            </Box>
          </Box>
        </Box>
      </Grid>
    ))}
  </Grid>
);

const FollowsTab: React.FC<FollowsTabProps> = ({ username: usernameProp, isPublic }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { username: usernameParam } = useParams<{ username: string }>();
  const queryParams = new URLSearchParams(location.search);
  const initialTab = (queryParams.get('follows') as FollowsTabType) || FOLLOWS_TABS.FOLLOWING;
  const initialPage = parseInt(queryParams.get('page') || '1', 10);

  const stableUsername = isPublic ? usernameParam : 'me';
  const username = isPublic ? usernameParam : usernameProp;

  const [tabValue, setTabValue] = useState<FollowsTabType>(initialTab);
  const [page, setPage] = useState(initialPage);

  // React Query でフォロワー取得
  const { data: followersData, isLoading: isLoadingFollowers } = useQuery({
    queryKey: ['followers', stableUsername, page],
    queryFn: () => getUserFollowers(username!, page),
    enabled: !!username,
    staleTime: isPublic ? 30000 : 2000,
  });

  // React Query でフォロー中取得
  const { data: followingData, isLoading: isLoadingFollowing } = useQuery({
    queryKey: ['following', stableUsername, page],
    queryFn: () => getUserFollowing(username!, page),
    enabled: !!username,
    staleTime: isPublic ? 30000 : 2000,
  });

  const followers: FollowUser[] = followersData?.results || [];
  const following: FollowUser[] = followingData?.results || [];
  const followerCount = followersData?.count || 0;
  const followingCount = followingData?.count || 0;
  const totalPages = tabValue === FOLLOWS_TABS.FOLLOWERS
    ? (followersData?.total_pages || 1)
    : (followingData?.total_pages || 1);

  const isLoading = tabValue === FOLLOWS_TABS.FOLLOWERS
    ? (isLoadingFollowers && !followersData)
    : (isLoadingFollowing && !followingData);

  // URLを更新する関数
  const updateUrl = (tab: FollowsTabType, page: number) => {
    const currentParams = new URLSearchParams(location.search);
    currentParams.set('follows', tab);
    currentParams.set('page', page.toString());

    navigate(`${location.pathname}?${currentParams.toString()}`, { replace: true });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const newTab = newValue === 0 ? FOLLOWS_TABS.FOLLOWING : FOLLOWS_TABS.FOLLOWERS;
    setTabValue(newTab);
    setPage(1);
    updateUrl(newTab, 1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    updateUrl(tabValue, value);
  };

  return (
    <Box>
      <Tabs
        value={tabValue === FOLLOWS_TABS.FOLLOWING ? 0 : 1}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ sx: { display: 'none' } }}
        sx={{
          minHeight: { xs: '32px', sm: '40px' },
          '& .MuiTab-root': {
            minHeight: { xs: '36px', sm: '40px' },
            py: { xs: 1, sm: 1.5 },
            px: { xs: 1, sm: 2 },
            fontSize: { xs: '0.65rem', sm: '0.75rem' },
            minWidth: 0,
            borderRadius: '12px',
            '&.Mui-selected': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
          },
        }}
      >
        <Tab label={`フォロー中 (${followingCount})`} />
        <Tab label={`フォロワー (${followerCount})`} />
      </Tabs>

      <Box sx={{ mt: 2 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={8}>
            {isLoading ? (
              <FollowsLoadingPlaceholder />
            ) : (
              <Grid container spacing={1}>
                {(tabValue === FOLLOWS_TABS.FOLLOWING ? following : followers).map((user) => (
                  <Grid item xs={12} key={user.username}>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        backdropFilter: 'blur(8px)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          transform: 'translateY(-1px)',
                        }
                      }}
                      onClick={() => navigate(`/profile/${user.username}`)}
                    >
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Avatar
                          src={user.avatar || undefined}
                          sx={{
                            width: 48,
                            height: 48,
                            border: '1px solid rgba(255, 255, 255, 0.08)'
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: '#ffffff',
                                mb: 0
                              }}
                            >
                              {user.display_name || user.username}
                            </Typography>
                            <UserPlanBadge planType={user.subscription_plan} />
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.75rem',
                              color: '#a7a7a7',
                              mb: 1
                            }}
                          >
                            @{user.username}
                          </Typography>
                          {user.bio && (
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.75rem',
                                color: 'rgba(255, 255, 255, 0.7)',
                                lineHeight: 1.4,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}
                            >
                              {user.bio}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}

            {totalPages > 1 && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default FollowsTab;
